import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { sinceChartStart } from "@/lib/chartWindow";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const RANGES = [
  { k: "today", l: "Today" },
  { k: "7", l: "7D" },
  { k: "30", l: "30D" },
  { k: "90", l: "90D" },
  { k: "180", l: "180D" },
  { k: "all", l: "All" },
];

function PricePanel({ title, subtitle, dataKey, color, gradId, points, testid, rangeLabel }) {
  const vals = points.map((p) => p[dataKey]).filter((v) => v != null);
  const min = vals.length ? Math.min(...vals) : 0;
  const max = vals.length ? Math.max(...vals) : 1;
  const pad = (max - min) * 0.12 || max * 0.05 || 1;
  const domainMin = Math.max(0, min - pad);
  const domainMax = max + pad;
  const step = Math.max(1e-9, (domainMax - domainMin) / 4);
  const dec = Math.max(0, Math.min(4, Math.ceil(-Math.log10(step)) + 1));
  const first = vals.length ? vals[0] : 0;
  const lastV = vals.length ? vals[vals.length - 1] : 0;
  const chg = first ? ((lastV - first) / first) * 100 : 0;
  const up = chg >= 0;
  return (
    <div className="glass p-4" data-testid={testid} style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <h4 className="font-display text-sm font-bold tracking-tight" style={{ color }}>{title}</h4>
        <span className="text-[11px] text-ink-3">{subtitle}</span>
        <span
          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold"
          style={{ background: up ? "rgba(0,134,58,0.12)" : "rgba(220,38,38,0.12)", color: up ? "#00863A" : "#DC2626" }}
          data-testid={`${testid}-change`}
        >
          {up ? "▲" : "▼"} {Math.abs(chg).toFixed(1)}% <span className="text-ink-3 font-sans font-normal">{rangeLabel}</span>
        </span>
      </div>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
            <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} stroke="#6B7280" />
            <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="#6B7280" width={56}
              domain={[domainMin, domainMax]} tickFormatter={(v) => `$${Number(v).toFixed(dec)}`} />
            <Tooltip
              formatter={(v) => [`$${Number(v).toFixed(4)}`, title]}
              contentStyle={{ border: "1px solid rgba(15,18,38,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)" }}
            />
            <Area type="monotone" dataKey={dataKey} name={title} stroke={color} strokeWidth={2.5} fill={`url(#${gradId})`} dot={false} isAnimationActive animationDuration={600} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function PriceHistoryModal({ open, onClose, currentCa, currentBot }) {
  const [range, setRange] = useState("30");
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("");
  const rangeLabel = (RANGES.find((r) => r.k === range) || {}).l || range;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/ca-price/history?range=${range}`)
      .then(({ data }) => { setPoints(sinceChartStart(data.points || [])); setSource(data.source || ""); })
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
  }, [open, range]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="price-history-modal" className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white text-ink-1">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp size={18} className="text-indigo-600" /> Price history — CA &amp; BOT
          </DialogTitle>
          <DialogDescription>
            Historical USD price of CaryPact (CA) and BotChain (BOT), anchored to CaryPact&apos;s official price.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-6 flex-wrap mb-1">
          <div data-testid="price-now-ca">
            <div className="kpi-label">CA now</div>
            <div className="num font-bold text-2xl" style={{ color: "#8D1CEB" }}>${Number(currentCa || 0).toFixed(2)}</div>
          </div>
          {currentBot != null && (
            <div data-testid="price-now-bot">
              <div className="kpi-label">BOT now</div>
              <div className="num font-bold text-2xl" style={{ color: "#15DCAC" }}>${Number(currentBot).toFixed(2)}</div>
            </div>
          )}
          <div className="ml-auto flex gap-1 p-1 rounded-xl" style={{ background: "rgba(15,18,38,0.05)" }} data-testid="price-range-toggle">
            {RANGES.map((r) => (
              <button
                key={r.k}
                onClick={() => setRange(r.k)}
                data-testid={`price-range-${r.k}`}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${range === r.k ? "bg-white shadow-sm text-ink" : "text-ink-2 hover:bg-white/50"}`}
              >
                {r.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto" /></div>
        ) : points.length === 0 ? (
          <div className="py-16 text-center text-ink-3 text-sm" data-testid="price-history-empty">Price history is unavailable right now — try again shortly.</div>
        ) : (
          <div className="space-y-3" data-testid="price-history-chart">
            <PricePanel title="CA (CaryPact)" subtitle="USD" dataKey="ca" color="#8D1CEB" gradId="caGrad" points={points} testid="price-chart-ca" rangeLabel={rangeLabel} />
            <PricePanel title="BOT (BotChain)" subtitle="USD" dataKey="bot" color="#15DCAC" gradId="botGrad" points={points} testid="price-chart-bot" rangeLabel={rangeLabel} />
          </div>
        )}
        <p className="text-[11px] text-ink-3 mt-2">{source || "CaryPact official price"} · CaryPact publishes only the current price, so the trend is anchored to on-chain data and refined daily with recorded CaryPact prices · not investment advice.</p>
      </DialogContent>
    </Dialog>
  );
}
